import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { EfficiencyTrackerCriteria } from "@app/types/dashboard/cards/efficiencyTracker/EfficiencyTrackerCardConfiguration";
import EntityTypesSupportedInEfficiencyTracker from "@app/types/dashboard/cards/efficiencyTracker/EntityTypesSupportedInEfficiencyTracker";
import Scope from "@app/types/dashboard/Scope";
import ActivityFieldName from "@app/util/fieldModel/ActivityFieldName";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import DealFieldName from "@app/util/fieldModel/DealFieldName";
import type FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";
import { convertFromPlatformFilterModel } from "@app/util/viewModel/convertFromPlatformFilterModel";

const buildEfficiencyDrillDownFilter = (params: {
  criteria: Pick<EfficiencyTrackerCriteria, "fieldType">;
  dateFieldName: string;
  entityType: EntityTypesSupportedInEfficiencyTracker;
  extraFilters: PlatformFilterModel | undefined;
  fieldModel: FieldModel;
  range: DateRange;
  scope: Scope | undefined;
  userIds: User["id"][];
  wonStageIds: Stage["id"][];
}): FilterModel => {
  const {
    criteria,
    dateFieldName,
    entityType,
    extraFilters,
    fieldModel,
    range,
    scope,
    userIds,
    wonStageIds,
  } = params;

  const filter: FilterModel = createAndCondition([
    convertFromPlatformFilterModel(extraFilters ?? {}, fieldModel),
    createSimpleCondition(dateFieldName, FilterOperator.IN_RANGE, [range.startDate, range.endDate]),
  ]);

  // Scope mirrors the card's getScopeFilters: activity→assignee, company/person→creator
  // (both over the expanded userIds), deal→team for team scope / user for user scope.
  if (entityType === EntityType.DEAL) {
    if (scope?.userId) {
      filter.conditions.push(
        createSimpleCondition(DealFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
      );
    } else if (scope?.teamId) {
      filter.conditions.push(
        createSimpleCondition(DealFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId])
      );
    }
  } else if (userIds.length) {
    const scopeField =
      entityType === EntityType.ACTIVITY ? ActivityFieldName.ASSIGNEE : CommonFieldName.CREATOR;
    filter.conditions.push(createSimpleCondition(scopeField, FilterOperator.IN_ANY, userIds));
  }

  if (criteria.fieldType === "facetime") {
    filter.conditions.push(
      createSimpleCondition(ActivityFieldName.RELIABILITY, FilterOperator.NOT_EMPTY)
    );
  } else if (criteria.fieldType === "wonRevenue" && wonStageIds.length) {
    filter.conditions.push(
      createSimpleCondition(DealFieldName.STAGE, FilterOperator.IN_ANY, wonStageIds)
    );
  }

  return filter;
};

export default buildEfficiencyDrillDownFilter;
